import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  makeStyles,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Grid,
} from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty';
import PlayCircleFilledIcon from '@material-ui/icons/PlayCircleFilled';
import CloseIcon from '@material-ui/icons/Close';
import RefreshIcon from '@material-ui/icons/Refresh';
import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { kubernetesApiRef } from '@backstage/plugin-kubernetes';
import { getTektonApiVersion } from '../types';

const useStyles = makeStyles(theme => ({
  logDialog: {
    '& .MuiDialog-paper': {
      height: '80vh',
      maxHeight: '80vh',
    },
  },
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(80vh - 130px)', // 减去标题和按钮的高度
    overflow: 'hidden',
  },
  dialogHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  refreshButton: {
    marginLeft: theme.spacing(1),
  },
  gridContainer: {
    flex: 1,
    overflow: 'hidden',
  },
  taskList: {
    borderRight: `1px solid ${theme.palette.divider}`,
    height: 'calc(80vh - 180px)',
    overflow: 'auto',
  },
  taskItem: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  taskItemSelected: {
    backgroundColor: theme.palette.action.selected,
    '&:hover': {
      backgroundColor: theme.palette.action.selected,
    },
  },
  logContainer: {
    backgroundColor: '#1e1e1e',
    color: '#d4d4d4',
    fontFamily: 'Monaco, Menlo, "Courier New", monospace',
    fontSize: '12px',
    padding: theme.spacing(2),
    borderRadius: 4,
    overflow: 'auto',
    height: 'calc(80vh - 200px)',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
  },
}));

// TaskRun 信息接口
export interface TaskRunInfo {
  name: string;
  pipelineTaskName: string;
  status: 'Succeeded' | 'Failed' | 'Running' | 'Pending';
  startTime?: string;
  completionTime?: string;
  podName?: string | null;
}

// PipelineRun 数据结构
export interface PipelineRunForLogs {
  apiVersion?: string;
  metadata: {
    name: string;
    namespace: string;
  };
  status?: {
    conditions?: Array<{
      status: string;
      type: string;
      reason?: string;
    }>;
    childReferences?: Array<{
      name: string;
      kind: string;
      pipelineTaskName: string;
    }>;
    taskRuns?: Record<string, any>;
  };
}

// Props 接口
interface PipelineRunLogsDialogProps {
  open: boolean;
  onClose: () => void;
  run: PipelineRunForLogs | null;
  clusterName: string;
  /** 是否启用实时轮询 */
  enablePolling?: boolean;
  /** 轮询间隔（毫秒）*/
  pollingInterval?: number;
  /** 初始选中的 Task 名称 */
  initialTaskName?: string;
}

// 日志查看对话框组件
export const PipelineRunLogsDialog: React.FC<PipelineRunLogsDialogProps> = ({
  open,
  onClose,
  run,
  clusterName,
  enablePolling = false,
  pollingInterval = 1000,
  initialTaskName,
}) => {
  const classes = useStyles();
  const kubernetesApi = useApi(kubernetesApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [selectedTask, setSelectedTask] = useState<(TaskRunInfo & { _logPath?: string }) | null>(null);
  const [logs, setLogs] = useState<string>('');
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [taskRuns, setTaskRuns] = useState<(TaskRunInfo & { _logPath?: string })[]>([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  // 日志缓存：taskName -> logs
  const logsCacheRef = useRef<Map<string, string>>(new Map());
  // 用于在轮询中访问最新的 selectedTask
  const selectedTaskRef = useRef<(TaskRunInfo & { _logPath?: string }) | null>(null);
  // 用于在轮询中访问最新的 taskRuns
  const taskRunsRef = useRef<(TaskRunInfo & { _logPath?: string })[]>([]);
  // 用户是否手动选择了任务（手动选择后不自动切换）
  const userManuallySelectedRef = useRef<boolean>(false);
  // 日志容器 ref，用于滚动控制
  const logContainerRef = useRef<HTMLDivElement | null>(null);
  // 用户是否手动滚动了日志（手动滚动后不自动滚动到底部）
  const userScrolledRef = useRef<boolean>(false);
  // 上一次日志内容，用于检测日志是否更新
  const prevLogsRef = useRef<string>('');

  // 同步 selectedTask 到 ref
  useEffect(() => {
    selectedTaskRef.current = selectedTask;
  }, [selectedTask]);

  // 同步 taskRuns 到 ref
  useEffect(() => {
    taskRunsRef.current = taskRuns;
  }, [taskRuns]);

  // 滚动到日志底部
  const scrollToBottom = useCallback(() => {
    if (logContainerRef.current && !userScrolledRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, []);

  // 检测用户是否手动滚动（如果滚动位置不在底部附近，认为用户手动滚动了）
  const handleLogScroll = useCallback(() => {
    if (!logContainerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = logContainerRef.current;
    // 如果距离底部超过 50px，认为用户手动滚动了
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 50;
    userScrolledRef.current = !isAtBottom;
  }, []);

  // 日志更新时自动滚动到底部（如果用户没有手动滚动）
  useEffect(() => {
    if (logs && logs !== prevLogsRef.current) {
      prevLogsRef.current = logs;
      // 使用 setTimeout 确保 DOM 更新后再滚动
      setTimeout(scrollToBottom, 0);
    }
  }, [logs, scrollToBottom]);

  // 获取 PipelineRun 是否正在运行
  const isRunning = useCallback(() => {
    if (!run?.status?.conditions) return false;
    const condition = run.status.conditions.find(c => c.type === 'Succeeded');
    if (!condition) return true;
    return condition.status === 'Unknown' && 
           ['Running', 'Started', 'Pending'].includes(condition.reason || '');
  }, [run]);

  // 从 Tekton Results API 获取 TaskRuns 列表
  // API 结构: /results -> /results/{result-id}/records -> 筛选 TaskRun 类型
  const fetchTaskRunsFromResults = useCallback(
    async (pipelineRunName: string, namespace: string): Promise<(TaskRunInfo & { _logPath?: string })[]> => {
      try {
        const proxyUrl = await discoveryApi.getBaseUrl('proxy');
        
        // 使用 filter 直接查询匹配的 result（通过 object.metadata.name annotation）
        const filter = `annotations["object.metadata.name"]=="${pipelineRunName}"`;
        const url = `${proxyUrl}/tekton-results/apis/results.tekton.dev/v1alpha2/parents/${namespace}/results?page_size=10&filter=${encodeURIComponent(filter)}`;
        
        const resultsResponse = await fetchApi.fetch(url);

        if (!resultsResponse.ok) {
          console.warn('Tekton Results API 获取 results 失败:', resultsResponse.status);
          return [];
        }

        const resultsData = await resultsResponse.json();
        const results = resultsData.results || [];

        // 查找 PipelineRun 类型的 result
        const matchedResult = results.find((r: any) => {
          const summaryType = r.summary?.type || '';
          return summaryType.includes('PipelineRun');
        });

        if (!matchedResult) {
          console.warn('未找到匹配的 result:', pipelineRunName);
          return [];
        }

        // 从 result.name 中提取 result-id（格式: namespace/results/uuid）
        const resultName = matchedResult.name;

        // 获取该 result 下的所有 records
        const recordsResponse = await fetchApi.fetch(
          `${proxyUrl}/tekton-results/apis/results.tekton.dev/v1alpha2/parents/${resultName}/records?page_size=100`,
        );

        if (!recordsResponse.ok) {
          console.warn('获取 records 失败:', recordsResponse.status, await recordsResponse.text());
          return [];
        }

        const recordsData = await recordsResponse.json();
        const records = recordsData.records || [];

        // 筛选出 TaskRun 类型的记录
        const tasks: (TaskRunInfo & { _logPath?: string })[] = [];
        for (const record of records) {
          // 检查 record 类型（可能是 tekton.dev/v1.TaskRun 或 tekton.dev/v1beta1.TaskRun）
          const recordType = record.data?.type || '';
          if (!recordType.includes('TaskRun')) continue;
          
          const taskRunData = record.data?.value;
          if (!taskRunData) continue;

          // 解析 TaskRun 数据（base64 编码的 JSON）
          let taskRun: any;
          try {
            const decoded = atob(taskRunData);
            taskRun = JSON.parse(decoded);
          } catch (e) {
            console.warn('解析 TaskRun 数据失败:', e);
            continue;
          }

          const condition = taskRun?.status?.conditions?.find((c: any) => c.type === 'Succeeded');
          let status: TaskRunInfo['status'] = 'Pending';
          if (condition) {
            if (condition.status === 'True') status = 'Succeeded';
            else if (condition.status === 'False') status = 'Failed';
            else if (condition.reason === 'Running') status = 'Running';
          }

          // 获取 log 路径（从 TaskRun 的 annotation 中）
          const logPath = taskRun.metadata?.annotations?.['results.tekton.dev/log'];

          tasks.push({
            name: taskRun.metadata?.name || '',
            pipelineTaskName: taskRun.metadata?.labels?.['tekton.dev/pipelineTask'] || taskRun.metadata?.name || '',
            status,
            startTime: taskRun.status?.startTime,
            completionTime: taskRun.status?.completionTime,
            podName: taskRun.status?.podName,
            _logPath: logPath,
          });
        }

        return tasks;
      } catch (error) {
        console.warn('从 Tekton Results API 获取 TaskRuns 失败:', error);
        return [];
      }
    },
    [discoveryApi, clusterName],
  );

  // 从 Tekton Results API 获取日志
  // 日志存储在 S3，通过 log 路径获取
  // logPath 格式: namespace/results/uuid/logs/taskrun-name
  const fetchLogsFromResults = useCallback(
    async (taskRunName: string, _namespace: string, logPath?: string): Promise<string | null> => {
      try {
        const proxyUrl = await discoveryApi.getBaseUrl('proxy');
        
        // 如果已有 logPath，直接获取日志
        if (logPath) {
          const logsResponse = await fetchApi.fetch(
            `${proxyUrl}/tekton-results/apis/results.tekton.dev/v1alpha2/parents/${logPath}`,
          );

          if (logsResponse.ok) {
            const logText = await logsResponse.text();
            return logText || null;
          }
          console.warn('通过 logPath 获取日志失败:', logsResponse.status);
          return null;
        }

        // 没有 logPath，无法获取日志
        console.warn('TaskRun 没有 logPath:', taskRunName);
        return null;
      } catch (error) {
        console.warn('从 Tekton Results API 获取日志失败:', error);
        return null;
      }
    },
    [discoveryApi, fetchApi],
  );

  // 获取 Pod 的所有容器日志
  const fetchPodLogs = useCallback(async (podName: string, namespace: string): Promise<string> => {
    try {
      const podResponse = await kubernetesApi.proxy({
        clusterName,
        path: `/api/v1/namespaces/${namespace}/pods/${podName}`,
      });

      if (!podResponse.ok) {
        return `Pod 不存在或无法访问: ${podResponse.status}`;
      }

      const podData = await podResponse.json();
      const containers = podData.spec?.containers || [];

      if (containers.length === 0) {
        return '未找到容器';
      }

      let allLogs = '';
      let hasError = false;
      for (const container of containers) {
        const logResponse = await kubernetesApi.proxy({
          clusterName,
          path: `/api/v1/namespaces/${namespace}/pods/${podName}/log?container=${container.name}&timestamps=true&tailLines=1000`,
        });

        if (logResponse.ok) {
          const logText = await logResponse.text();
          if (logText) {
            allLogs += `\n========== ${container.name} ==========\n`;
            allLogs += logText;
          }
        } else {
          hasError = true;
          allLogs += `\n========== ${container.name} ==========\n`;
          allLogs += `获取日志失败: ${logResponse.status}\n`;
        }
      }

      if (!allLogs.trim()) {
        return hasError ? '获取日志失败' : '容器暂无日志输出';
      }

      return allLogs;
    } catch (error) {
      return `获取日志异常: ${error}`;
    }
  }, [kubernetesApi, clusterName]);

  // 加载日志（带缓存）
  // silent: 静默刷新，不显示加载状态（用于轮询）
  // 策略：
  // - 正在运行的任务 → K8s API（实时日志）
  // - 已完成的任务 → Tekton Results API（历史日志）
  const fetchLogs = useCallback(
    async (task: TaskRunInfo & { _logPath?: string }, forceRefresh = false, silent = false) => {
      if (!run) return;

      // 如果任务已完成且有缓存，直接使用缓存
      const isTaskCompleted = task.status === 'Succeeded' || task.status === 'Failed';
      const isTaskRunning = task.status === 'Running' || task.status === 'Pending';
      const cachedLogs = logsCacheRef.current.get(task.name);
      if (isTaskCompleted && cachedLogs && !forceRefresh) {
        setLogs(cachedLogs);
        return;
      }

      // 非静默模式：先清空日志并显示加载状态
      if (!silent) {
        setLogs('');
        setLoadingLogs(true);
      }

      try {
        let logContent = '';

        if (isTaskRunning) {
          // 正在运行的任务：直接使用 K8s API 获取实时日志
          if (task.podName) {
            logContent = await fetchPodLogs(task.podName, run.metadata.namespace);
          } else {
            const podResponse = await kubernetesApi.proxy({
              clusterName,
              path: `/api/v1/namespaces/${run.metadata.namespace}/pods?labelSelector=tekton.dev/taskRun=${task.name}`,
            });

            if (podResponse.ok) {
              const podData = await podResponse.json();
              if (podData.items && podData.items.length > 0) {
                const pod = podData.items[0];
                logContent = await fetchPodLogs(pod.metadata.name, run.metadata.namespace);
              } else {
                logContent = '未找到相关 Pod，任务可能还未开始';
              }
            } else {
              logContent = `获取 Pod 列表失败: ${podResponse.status}`;
            }
          }
          logContent = `[来源: K8s API - 实时]\n${logContent}`;
        } else if (isTaskCompleted) {
          // 已完成的任务：优先使用 Tekton Results API（传入 _logPath 加速查询）
          const resultsLogs = await fetchLogsFromResults(task.name, run.metadata.namespace, task._logPath);
          if (resultsLogs) {
            logContent = `[来源: Tekton Results API]\n${resultsLogs}`;
          } else {
            // Results API 没有数据，回退到 K8s API（Pod 可能还未被清理）
            if (task.podName) {
              logContent = await fetchPodLogs(task.podName, run.metadata.namespace);
            } else {
              const podResponse = await kubernetesApi.proxy({
                clusterName,
                path: `/api/v1/namespaces/${run.metadata.namespace}/pods?labelSelector=tekton.dev/taskRun=${task.name}`,
              });

              if (podResponse.ok) {
                const podData = await podResponse.json();
                if (podData.items && podData.items.length > 0) {
                  const pod = podData.items[0];
                  logContent = await fetchPodLogs(pod.metadata.name, run.metadata.namespace);
                } else {
                  logContent = '未找到相关 Pod，日志可能已被清理';
                }
              } else {
                logContent = `获取 Pod 列表失败: ${podResponse.status}`;
              }
            }
            logContent = `[来源: K8s API - 回退]\n${logContent}`;
          }
        } else {
          // 未知状态：尝试从 K8s API 获取
          logContent = `[任务状态: ${task.status}]\n`;
          if (task.podName) {
            logContent += await fetchPodLogs(task.podName, run.metadata.namespace);
          } else {
            logContent += '任务状态未知，无法获取日志';
          }
        }

        // 缓存已完成任务的日志
        if (isTaskCompleted) {
          logsCacheRef.current.set(task.name, logContent);
        }

        setLogs(logContent);
      } catch (error) {
        setLogs(`获取日志失败: ${error}`);
      } finally {
        if (!silent) {
          setLoadingLogs(false);
        }
      }
    },
    [run, kubernetesApi, clusterName, fetchPodLogs, fetchLogsFromResults],
  );

  // 自动选择最新的运行中任务
  const selectLatestRunningTask = useCallback((tasks: (TaskRunInfo & { _logPath?: string })[]) => {
    // 如果用户手动选择了任务，不自动切换
    if (userManuallySelectedRef.current) {
      return;
    }

    // 找到正在运行的任务（优先）或最后一个任务
    const runningTask = tasks.find(t => t.status === 'Running');
    if (runningTask) {
      // 如果当前选中的不是这个运行中的任务，切换到它
      if (selectedTaskRef.current?.name !== runningTask.name) {
        setSelectedTask(runningTask);
      }
      return;
    }
    
    // 没有运行中的任务，选择最后一个（最新开始的）
    const lastTask = tasks[tasks.length - 1];
    if (lastTask && selectedTaskRef.current?.name !== lastTask.name) {
      // 只在当前选中的任务已完成时才切换
      const currentTask = selectedTaskRef.current;
      if (!currentTask || currentTask.status === 'Succeeded' || currentTask.status === 'Failed') {
        setSelectedTask(lastTask);
      }
    }
  }, []);

  // 加载 TaskRuns
  // 策略：
  // - 正在运行的 PipelineRun → K8s API（获取最新状态和新增 task）
  // - 已完成的 PipelineRun → Tekton Results API
  // silent: 静默刷新，不显示加载状态（用于轮询）
  // targetTaskName: 指定要选中的任务名称
  const loadTaskRuns = useCallback(
    async (autoSelectFirst = false, silent = false, targetTaskName?: string) => {
      if (!run) return;
      if (!silent) {
        setLoadingTasks(true);
      }
      try {
        let tasks: (TaskRunInfo & { _logPath?: string })[] = [];
        const pipelineRunning = isRunning();
        // 根据 PipelineRun 的 apiVersion 确定使用的 Tekton API 版本
        const tektonApiVersion = getTektonApiVersion(run.apiVersion);

        if (pipelineRunning) {
          // 正在运行的 PipelineRun：从 K8s API 获取最新状态（包括新增的 task）
          try {
            const taskRunsResponse = await kubernetesApi.proxy({
              clusterName,
              path: `/apis/${tektonApiVersion}/namespaces/${run.metadata.namespace}/taskruns?labelSelector=tekton.dev/pipelineRun=${run.metadata.name}`,
            });

            if (taskRunsResponse.ok) {
              const taskRunsData = await taskRunsResponse.json();
              for (const tr of taskRunsData.items || []) {
                const condition = tr?.status?.conditions?.find(
                  (c: any) => c.type === 'Succeeded',
                );
                let status: TaskRunInfo['status'] = 'Pending';
                if (condition) {
                  if (condition.status === 'True') status = 'Succeeded';
                  else if (condition.status === 'False') status = 'Failed';
                  else if (condition.reason === 'Running') status = 'Running';
                }

                tasks.push({
                  name: tr.metadata.name,
                  pipelineTaskName:
                    tr.metadata.labels?.['tekton.dev/pipelineTask'] ||
                    tr.metadata.name,
                  status,
                  startTime: tr?.status?.startTime,
                  completionTime: tr?.status?.completionTime,
                  podName: tr?.status?.podName,
                  _logPath: tr.metadata?.annotations?.['results.tekton.dev/log'],
                });
              }
            }
          } catch {
            // 忽略错误
          }
        } else {
          // 已完成的 PipelineRun：从 Tekton Results API 获取
          tasks = await fetchTaskRunsFromResults(run.metadata.name, run.metadata.namespace);
        }

        // 如果 Tekton Results API 没有数据，回退到 K8s API
        if (tasks.length === 0) {
          try {
            const taskRunsResponse = await kubernetesApi.proxy({
              clusterName,
              path: `/apis/${tektonApiVersion}/namespaces/${run.metadata.namespace}/taskruns?labelSelector=tekton.dev/pipelineRun=${run.metadata.name}`,
            });

            if (taskRunsResponse.ok) {
              const taskRunsData = await taskRunsResponse.json();
              for (const tr of taskRunsData.items || []) {
                const condition = tr?.status?.conditions?.find(
                  (c: any) => c.type === 'Succeeded',
                );
                let status: TaskRunInfo['status'] = 'Pending';
                if (condition) {
                  if (condition.status === 'True') status = 'Succeeded';
                  else if (condition.status === 'False') status = 'Failed';
                  else if (condition.reason === 'Running') status = 'Running';
                }

                tasks.push({
                  name: tr.metadata.name,
                  pipelineTaskName:
                    tr.metadata.labels?.['tekton.dev/pipelineTask'] ||
                    tr.metadata.name,
                  status,
                  startTime: tr?.status?.startTime,
                  completionTime: tr?.status?.completionTime,
                  podName: tr?.status?.podName,
                  _logPath: tr.metadata?.annotations?.['results.tekton.dev/log'],
                });
              }
            }
          } catch {
            // 忽略错误
          }
        }

        // 如果还是没有数据，尝试从传入的 run 对象获取
        if (tasks.length === 0 && run.status?.childReferences) {
          for (const child of run.status.childReferences) {
            if (child.kind === 'TaskRun') {
              tasks.push({
                name: child.name,
                pipelineTaskName: child.pipelineTaskName,
                status: 'Pending',
              });
            }
          }
        }

        // 按 startTime 排序
        tasks.sort((a, b) => {
          if (!a.startTime) return 1;
          if (!b.startTime) return -1;
          return new Date(a.startTime).getTime() - new Date(b.startTime).getTime();
        });

        setTaskRuns(tasks);

        // 首次加载或轮询时自动选择合适的任务
        if (tasks.length > 0) {
          if (targetTaskName) {
            // 如果指定了目标任务，优先选择它
            const targetTask = tasks.find(
              t => t.pipelineTaskName === targetTaskName || t.name === targetTaskName
            );
            if (targetTask) {
              setSelectedTask(targetTask);
              return;
            }
          }
          
          if (autoSelectFirst) {
            // 首次加载：选择正在运行的任务，或最后一个任务（最新的）
            const runningTask = tasks.find(t => t.status === 'Running');
            setSelectedTask(runningTask || tasks[tasks.length - 1]);
          } else if (silent) {
            // 轮询更新：自动切换到最新的运行中任务
            selectLatestRunningTask(tasks);
          }
        }
      } finally {
        if (!silent) {
          setLoadingTasks(false);
        }
      }
    },
    [run, kubernetesApi, clusterName, isRunning, fetchTaskRunsFromResults, selectLatestRunningTask],
  );

  // 停止轮询
  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  // 启动轮询（只在运行中的 PipelineRun 时启用）
  const startPolling = useCallback(() => {
    if (!enablePolling || !isRunning()) return;

    stopPolling();
    pollIntervalRef.current = setInterval(async () => {
      // 先检查 PipelineRun 是否还在运行
      // 如果已完成，立即停止轮询
      if (!isRunning()) {
        stopPolling();
        // 最后刷新一次任务列表，获取最终状态
        await loadTaskRuns(false, true);
        return;
      }

      // 静默刷新任务列表状态
      await loadTaskRuns(false, true);
      
      // 刷新当前选中任务的日志（如果任务正在运行）
      const currentTask = selectedTaskRef.current;
      if (currentTask && (currentTask.status === 'Running' || currentTask.status === 'Pending')) {
        // 强制刷新日志，静默模式
        await fetchLogs(currentTask, true, true);
      }
      
      // 检查是否所有任务都已完成，如果是则停止轮询
      const currentTasks = taskRunsRef.current;
      const allTasksCompleted = currentTasks.length > 0 && currentTasks.every(
        t => t.status === 'Succeeded' || t.status === 'Failed'
      );
      if (allTasksCompleted) {
        stopPolling();
      }
    }, pollingInterval);
  }, [enablePolling, isRunning, stopPolling, loadTaskRuns, fetchLogs, pollingInterval]);

  // 当 run 变化时重置状态
  useEffect(() => {
    setSelectedTask(null);
    setTaskRuns([]);
    setLogs('');
    logsCacheRef.current.clear();
    userManuallySelectedRef.current = false; // 重置手动选择标记
    userScrolledRef.current = false; // 重置滚动状态
    prevLogsRef.current = ''; // 重置日志记录
    stopPolling();
  }, [run, stopPolling]);

  // 选择任务时加载日志
  useEffect(() => {
    if (selectedTask && open) {
      // 切换任务时重置滚动状态，允许自动滚动到底部
      userScrolledRef.current = false;
      fetchLogs(selectedTask);
    }
  }, [selectedTask, open, fetchLogs]);

  // 打开对话框时加载 TaskRuns
  useEffect(() => {
    if (open && run) {
      loadTaskRuns(true, false, initialTaskName);
    }
  }, [open, run, loadTaskRuns, initialTaskName]);

  // 启动/停止轮询 - 根据 open 状态控制
  useEffect(() => {
    if (open && enablePolling && isRunning()) {
      startPolling();
    } else {
      // 对话框关闭或不需要轮询时，立即停止
      stopPolling();
    }
    return () => stopPolling();
  }, [open, enablePolling, isRunning, startPolling, stopPolling]);

  // 组件卸载时清理
  useEffect(() => {
    return () => {
      // 确保组件卸载时停止轮询
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, []);

  // 获取任务状态图标
  const getTaskStatusIcon = (status: TaskRunInfo['status']) => {
    switch (status) {
      case 'Succeeded': return <CheckCircleIcon style={{ color: '#4caf50', fontSize: 20 }} />;
      case 'Failed': return <ErrorIcon style={{ color: '#f44336', fontSize: 20 }} />;
      case 'Running': return <PlayCircleFilledIcon style={{ color: '#2196f3', fontSize: 20 }} />;
      default: return <HourglassEmptyIcon style={{ color: '#9e9e9e', fontSize: 20 }} />;
    }
  };

  if (!run) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      className={classes.logDialog}
    >
      <DialogTitle>
        <Box className={classes.dialogHeader}>
          <Typography variant="h6">
            日志查看: {run.metadata.name}
          </Typography>
          <Box>
            <Tooltip title="刷新日志">
              <IconButton
                size="small"
                onClick={() => {
                  loadTaskRuns(false);
                  if (selectedTask) fetchLogs(selectedTask, true);
                }}
                className={classes.refreshButton}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <IconButton size="small" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <Grid container spacing={2} className={classes.gridContainer}>
          {/* 任务列表 */}
          <Grid item xs={3}>
            <Typography variant="subtitle2" gutterBottom>
              Tasks ({taskRuns.length})
            </Typography>
            {loadingTasks ? (
              <Box display="flex" justifyContent="center" p={2}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <List className={classes.taskList} dense>
                {taskRuns.map((task) => (
                  <ListItem
                    key={task.name}
                    button
                    className={`${classes.taskItem} ${selectedTask?.name === task.name ? classes.taskItemSelected : ''}`}
                    onClick={() => {
                      userManuallySelectedRef.current = true; // 标记用户手动选择
                      setSelectedTask(task);
                    }}
                  >
                    <ListItemIcon style={{ minWidth: 32 }}>
                      {getTaskStatusIcon(task.status)}
                    </ListItemIcon>
                    <ListItemText
                      primary={task.pipelineTaskName}
                      secondary={task.status}
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Grid>

          {/* 日志内容 */}
          <Grid item xs={9}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="subtitle2">
                {selectedTask ? `${selectedTask.pipelineTaskName} 日志` : '选择一个任务查看日志'}
              </Typography>
            </Box>
            {loadingLogs ? (
              <Box 
                className={classes.logContainer} 
                display="flex" 
                flexDirection="column"
                justifyContent="center" 
                alignItems="center"
                minHeight={300}
              >
                <CircularProgress size={40} />
                <Typography variant="body2" style={{ marginTop: 16, color: '#9e9e9e' }}>
                  正在加载日志...
                </Typography>
              </Box>
            ) : (
              <div 
                className={classes.logContainer}
                ref={logContainerRef}
                onScroll={handleLogScroll}
              >
                {logs || '暂无日志'}
              </div>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          关闭
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PipelineRunLogsDialog;
